import {
  BatiFlowThemeProvider,
  loadThemePreference,
  resolveTheme,
  saveThemePreference,
  type ThemePreference,
} from '@batiflow/ui';
import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from 'react';
import { useMediaQuery } from '@/shared/hooks/use-media-query';
import { useDirection } from './i18n-provider';
import { useRuntime } from './runtime-provider';

interface ThemePreferenceValue {
  preference: ThemePreference;
  setPreference: (preference: ThemePreference) => void;
}

const ThemePreferenceContext = createContext<ThemePreferenceValue | null>(null);

/** The saved theme choice (light, dark, high contrast or system) and its setter. */
export function useThemePreference(): ThemePreferenceValue {
  const value = useContext(ThemePreferenceContext);
  if (!value) throw new Error('useThemePreference needs a <ThemeProvider>');
  return value;
}

/** BatiFlow Fluent theme: follows the saved choice or the system, and the text direction. */
export function ThemeProvider({ children }: { children?: ReactNode }) {
  const { storage } = useRuntime();
  const dir = useDirection();
  const [preference, setPreferenceState] = useState(() => loadThemePreference(storage));
  const prefersDark = useMediaQuery('(prefers-color-scheme: dark)');
  const prefersHighContrast = useMediaQuery('(forced-colors: active)');

  const setPreference = useCallback(
    (next: ThemePreference) => {
      setPreferenceState(next);
      saveThemePreference(storage, next);
    },
    [storage],
  );
  const value = useMemo(() => ({ preference, setPreference }), [preference, setPreference]);
  const theme = resolveTheme(preference, { prefersDark, prefersHighContrast });

  return (
    <ThemePreferenceContext.Provider value={value}>
      <BatiFlowThemeProvider theme={theme} dir={dir}>
        {children}
      </BatiFlowThemeProvider>
    </ThemePreferenceContext.Provider>
  );
}
